/**
 * Invisible Cloudflare Turnstile for the public cloud tools (W.4.48).
 *
 * The public tools stay free and account-free (access-model Tier 1), so the
 * only thing standing between a script and our LLM bill is proof that a
 * browser — not curl — is driving. Turnstile in managed mode is silent for
 * effectively every real visitor, which is why it is acceptable here where an
 * interactive captcha would not be (see internal/specs/host-product-spec.md).
 *
 * Ships dark: with no `VITE_TURNSTILE_SITE_KEY` every call resolves to null
 * and nothing about the tool flow changes.
 */

import { Effect } from 'effect';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';
/** A solve should take well under a second; never hang the upload on it. */
const SOLVE_TIMEOUT_MS = 8000;

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'timeout-callback'?: () => void;
      appearance?: 'always' | 'execute' | 'interaction-only';
      size?: 'normal' | 'flexible' | 'invisible';
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function turnstileSiteKeyEffect(): Effect.Effect<string> {
  return Effect.sync(() => {
    const key = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    return typeof key === 'string' ? key.trim() : '';
  });
}

export function turnstileSiteKey(): string {
  return Effect.runSync(turnstileSiteKeyEffect());
}

export function isTurnstileConfiguredEffect(): Effect.Effect<boolean> {
  return turnstileSiteKeyEffect().pipe(Effect.map((key) => key.length > 0));
}

export function isTurnstileConfigured(): boolean {
  return Effect.runSync(isTurnstileConfiguredEffect());
}

let scriptPromise: Promise<TurnstileApi | null> | null = null;

function loadTurnstileScript(): Promise<TurnstileApi | null> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi | null>((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);

    const existing = document.getElementById(SCRIPT_ID);
    const script = existing instanceof HTMLScriptElement ? existing : document.createElement('script');
    const settle = () => resolve(window.turnstile ?? null);

    script.addEventListener('load', settle, { once: true });
    script.addEventListener('error', () => resolve(null), { once: true });

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
}

/**
 * Solve one invisible challenge and return the token.
 *
 * Resolves null — never rejects — when Turnstile is unconfigured, fails to
 * load, errors, or takes too long. The server decides what an absent token
 * means; a client-side failure must not be able to wedge the tool.
 *
 * Tokens are single-use, so this always runs a fresh solve rather than caching.
 */
export const solveTurnstileEffect = Effect.fn('web.turnstile.solve')(function* () {
  const sitekey = yield* turnstileSiteKeyEffect();
  if (!sitekey) return null;

  const api = yield* Effect.promise(() => loadTurnstileScript());
  if (!api) return null;

  return yield* Effect.promise(
    () =>
      new Promise<string | null>((resolve) => {
        const container = document.createElement('div');
        // Rendered off-screen rather than display:none — Turnstile refuses to run
        // in a container it considers hidden.
        container.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;left:-9999px;';
        document.body.appendChild(container);

        let settled = false;
        let widgetId: string | undefined;

        const cleanup = () => {
          if (widgetId !== undefined) {
            try {
              api.remove(widgetId);
            } catch {
              // Widget already torn down — nothing to reclaim.
            }
          }
          container.remove();
        };

        const settle = (token: string | null) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(token);
          // Deferred so a callback fired synchronously from render() still tears
          // down the widget — `widgetId` is not assigned until render returns.
          queueMicrotask(cleanup);
        };

        const timer = setTimeout(() => settle(null), SOLVE_TIMEOUT_MS);

        try {
          widgetId = api.render(container, {
            sitekey,
            appearance: 'interaction-only',
            size: 'invisible',
            callback: (token: string) => settle(token),
            'error-callback': () => settle(null),
            'timeout-callback': () => settle(null),
          });
        } catch {
          settle(null);
        }
      }),
  );
});

export async function solveTurnstile(): Promise<string | null> {
  return Effect.runPromise(solveTurnstileEffect());
}
